// GwbkAutogenDialog — "Autogenerate from gwbk" flow.
//
// Two phases inside one modal:
//   1. Setup — pick a gateway backup (gwbk) + capture options, then Start
//      (see `GwbkAutogenSetupForm`).
//   2. Running — poll the job through its stages (capture → plan → build) via
//      the shared `GwbkAutogenJobView`, with Stop / Download / Start-another.
//
// The job runs end-to-end: it screenshots every Perspective route, plans which
// dashboards to build, then spins up a dashboard_builder agent per planned view
// to recreate it with live data. See `dashboard-autogen-routes.ts`.

import { useEffect, useState } from 'react'
import { Camera, Download, X } from 'lucide-react'
import {
  gwbkAutogenDownloadUrl,
  useCancelGwbkAutogenMutation,
  useGetGwbkAutogenJobQuery
} from '@axilon-apps/domains-ontology-ui-state'

import { Button } from '../../components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../../components/ui/dialog'
import GwbkAutogenJobView from './GwbkAutogenJobView'
import GwbkAutogenSetupForm from './GwbkAutogenSetupForm'
import { isActiveState } from './gwbk-autogen-status'

export interface GwbkAutogenDialogProps {
  open: boolean
  onClose: () => void
  /** Called once a run has started, with its job id (e.g. to select it elsewhere). */
  onStarted?: (jobId: string) => void
}

const GwbkAutogenDialog = ({ open, onClose, onStarted }: GwbkAutogenDialogProps) => {
  const [jobId, setJobId] = useState<string | null>(null)

  const [cancelAutogen, cancelState] = useCancelGwbkAutogenMutation()

  const jobQuery = useGetGwbkAutogenJobQuery(jobId ?? '', {
    skip: !jobId
  })
  const job = jobQuery.data
  const isActive = isActiveState(job?.state)

  // Reset to the setup phase when the dialog is closed so reopening starts clean.
  useEffect(() => {
    if (!open) setJobId(null)
  }, [open])

  const handleStarted = (startedJobId: string) => {
    setJobId(startedJobId)
    onStarted?.(startedJobId)
  }

  const handleCancel = async () => {
    if (!jobId) return
    try {
      await cancelAutogen(jobId).unwrap()
    } catch {
      // Best-effort; the poll will reflect the real state.
    }
  }

  const showSetup = jobId === null

  return (
    <Dialog open={open} onOpenChange={(next) => (!next ? onClose() : undefined)}>
      <DialogContent className="flex max-h-[85vh] flex-col sm:max-w-170">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Camera className="size-4" aria-hidden />
            Autogenerate from gwbk
          </DialogTitle>
        </DialogHeader>

        {showSetup ? (
          <GwbkAutogenSetupForm onClose={onClose} onStarted={handleStarted} />
        ) : (
          <>
            {job ? <GwbkAutogenJobView job={job} /> : null}
            <DialogFooter>
              {isActive ? (
                <Button
                  variant="outline"
                  onClick={() => void handleCancel()}
                  disabled={cancelState.isLoading}
                >
                  <X className="size-4" aria-hidden />
                  Stop
                </Button>
              ) : (
                <Button variant="outline" onClick={() => setJobId(null)}>
                  Start another
                </Button>
              )}
              {job && job.shots.length > 0 ? (
                <Button asChild>
                  <a href={gwbkAutogenDownloadUrl(job.id)}>
                    <Download className="size-4" aria-hidden />
                    Download all
                  </a>
                </Button>
              ) : null}
              <Button variant="ghost" onClick={onClose}>
                Close
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default GwbkAutogenDialog
